import { Component, ChangeDetectionStrategy, signal, computed, inject, effect } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { UserRoleService } from '../../services/user-role.service';
import { PortalUiService } from '../../services/portal-ui.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CdkMenuModule } from '@angular/cdk/menu';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChipModule } from 'primeng/chip';
import { FooterComponent } from '../footer/footer';
import { TranslatePipe } from '../../core/features/translations';
import { filter, tap } from 'rxjs';
import { ResizableDirective, ResizeHandleDirective, ResizeEvent } from 'angular-resizable-element';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

/**
 * Layout Component
 *
 * Main layout wrapper for authenticated pages with:
 * - Persistent navigation sidebar (Tailwind + CDK)
 * - Header with logout
 * - Router outlet for page content
 * - Footer component
 * - Role-based navigation
 */
@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CdkMenuModule,
    CdkScrollable,
    ChipModule,
    FooterComponent,
    TranslatePipe,
    ResizableDirective,
    ResizeHandleDirective,
  ],
  templateUrl: './layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  private readonly authService = inject(AuthService);
  protected readonly roleService = inject(UserRoleService);
   private readonly uiService = inject(PortalUiService);
   private readonly router = inject(Router);
   private readonly breakpointObserver = inject(BreakpointObserver);
 
   // Breakpoint signals for UI adjustments (beyond width)
   readonly isMobile = toSignal(this.breakpointObserver.observe('(max-width: 768px)'), { initialValue: { matches: false, breakpoints: {} } });
   readonly isTablet = toSignal(this.breakpointObserver.observe('(max-width: 1024px)'), { initialValue: { matches: false, breakpoints: {} } });
 
   /**
    * Effect to set smart defaults on screen resize.
    * Only auto-collapses when crossing the 1024px threshold.
    */
   private readonly breakpointEffect = effect(() => {
     const tablet = this.isTablet().matches;
     // When entering tablet/mobile range, auto-collapse
     if (tablet) {
       this.collapsed.set(true);
     } else {
       // When entering desktop range, auto-expand
       this.collapsed.set(false);
     }
   }, { allowSignalWrites: true });
 
   readonly collapsed = signal(false);
  readonly sidebarWidth = signal(256); // Default 256px
  readonly einsatzplanungExpanded = signal(false);

  // Expose UI service signals
  readonly pageCount = this.uiService.pageCount;

  // Track current route for dynamic page title
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => this.uiService.reset()), // Reset stats on every navigation
    ),
  );

  // Compute page title translation key based on current route
  readonly pageTitle = computed(() => {
    // Trigger reactivity by accessing currentUrl signal
    this.currentUrl();

    const url = this.router.url;
    if (url.includes('/app/availability')) {
      return 'portal.nav.availability';
    }
    if (url.includes('/app/einsatzplanung')) {
      return 'portal.nav.scheduling';
    }
    if (url.includes('/app/dashboard')) {
      return 'portal.nav.dashboard';
    }
    if (url.includes('/app/my-data')) {
      return 'portal.nav.mydata';
    }
    if (url.includes('/app/degrees')) {
      return 'contacts.degrees.title';
    }
    if (url.includes('/app/modules')) {
      return 'portal.degrees.modules.title';
    }
    if (url.includes('/app/lectures')) {
      return 'portal.nav.lectures';
    }
    if (url.includes('/app/rechnungen')) {
      return 'generic.invoices';
    }
    if (url.includes('/app/unterlagen')) {
      return 'portal.nav.documents';
    }
    if (url.includes('/app/teachability')) {
      return 'portal.nav.teachability';
    }
    if (url.includes('/app/planning-overview')) {
      return 'portal.nav.planning_overview';
    }
    if (url.includes('/app/confirmed-plan')) {
      return 'portal.nav.confirmed_plan';
    }
    // COMMENTED OUT: Vorerst nicht erreichbar
    /*
    if (url.includes('/app/planning')) {
      return 'portal.nav.planning';
    }
    if (url.includes('/app/abrechnungen')) {
      return 'portal.nav.billing';
    }
    */
    if (url.includes('/app/settings')) {
      return 'portal.header.menu.settings';
    }
    return null;
  });

   // Computed values
   readonly sidebarWidthStyle = computed(() => {
     // 1. Mobile Case: Drawer behavior (0px to 280px)
     if (this.isMobile().matches) {
       return this.collapsed() ? '0px' : '280px';
     }
 
     // 2. Tablet/Desktop Case: Minimized (64px) to Expanded
     return this.collapsed() ? '64px' : `${this.sidebarWidth()}px`;
   });

  onResizeEnd(event: ResizeEvent): void {
    if (event.rectangle.width && event.rectangle.width >= 64 && event.rectangle.width <= 600) {
      this.sidebarWidth.set(event.rectangle.width);
      if (event.rectangle.width < 100) {
        this.collapsed.set(true);
      } else if (this.collapsed()) {
        this.collapsed.set(false);
      }
    }
  }

  toggleSidebar(): void {
    this.collapsed.update((value) => !value);
  }

  toggleEinsatzplanung(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.einsatzplanungExpanded.update((value) => !value);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
